import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Headset, Send, Loader2, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SupportChatButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/support/my-messages`, { withCredentials: true });
      setMessages(res.data || []);
      setUnread(0);
    } catch {}
  };

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API}/support/my-unread`, { withCredentials: true });
      setUnread(res.data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    if (user) fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchMessages();
      pollRef.current = setInterval(fetchMessages, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/send`, { content: newMsg.trim() }, { withCredentials: true });
      setNewMsg("");
      fetchMessages();
    } catch { toast.error("Errore invio messaggio"); }
    finally { setSending(false); }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#FF4A00] text-white shadow-lg flex items-center justify-center hover:bg-[#E04400] transition-all hover:scale-110 border-2 border-[#09090B]"
        data-testid="support-chat-btn"
      >
        <Headset className="h-6 w-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
            {unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col p-0 border-2 border-[#09090B] rounded-2xl shadow-[4px_4px_0_#09090B] overflow-hidden">
          {/* Header */}
          <div className="bg-[#09090B] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#FF4A00] flex items-center justify-center">
                <Headset className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-sm" data-testid="support-chat-title">Assistenza BookEasy</h3>
                <p className="text-[10px] text-white/60">Ti rispondiamo al piu presto</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[300px] max-h-[400px] bg-[#FDFBF7]">
            {messages.length === 0 && (
              <div className="text-center text-sm text-[#6A564A] py-10">
                <Headset className="h-8 w-8 mx-auto mb-2 text-[#FF4A00]" />
                <p className="font-bold">Ciao! Come possiamo aiutarti?</p>
                <p className="text-xs mt-1">Scrivi il tuo messaggio qui sotto</p>
              </div>
            )}
            {messages.map(m => (
              <div
                key={m.message_id}
                className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.sender === "user"
                      ? "bg-[#FF4A00] text-white rounded-br-md"
                      : "bg-white border border-slate-200 text-[#09090B] rounded-bl-md shadow-sm"
                  }`}
                  data-testid={`support-msg-${m.message_id}`}
                >
                  {m.sender === "admin" && (
                    <p className="text-[10px] font-bold text-[#FF4A00] mb-0.5">{m.admin_name || "Assistenza"}</p>
                  )}
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  <p className={`text-[9px] mt-1 ${m.sender === "user" ? "text-white/60" : "text-[#9CA3AF]"}`}>
                    {new Date(m.created_at).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-white border-t border-slate-200 flex gap-2">
            <Input
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Scrivi un messaggio..."
              className="rounded-xl border-slate-200 h-10 text-sm"
              disabled={sending}
              data-testid="support-chat-input"
            />
            <Button
              onClick={send}
              disabled={sending || !newMsg.trim()}
              className="rounded-xl bg-[#FF4A00] text-white hover:bg-[#E04400] border-0 h-10 px-4"
              data-testid="support-chat-send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
